import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { apiService } from '../services/apiService';

const API_KEY_FIELDS = [
  { key: 'openaiApiKey', label: 'OpenAI / LLM API key' },
  { key: 'stripeSecretKey', label: 'Stripe secret key' },
  { key: 'outboundApiToken', label: 'Outbound API token' },
];

const SuperAdminSystemConfigurationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [siteDisplayName, setSiteDisplayName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  const [outboundWebhookUrl, setOutboundWebhookUrl] = useState('');
  const [slackIncomingWebhook, setSlackIncomingWebhook] = useState('');
  const [customIntegrationNotes, setCustomIntegrationNotes] = useState('');

  const [aiAgent, setAiAgent] = useState(true);
  const [payments, setPayments] = useState(true);
  const [helpChat, setHelpChat] = useState(true);
  const [swaggerUi, setSwaggerUi] = useState(true);

  const [apiKeyInputs, setApiKeyInputs] = useState<Record<string, string>>({
    openaiApiKey: '',
    stripeSecretKey: '',
    outboundApiToken: '',
  });
  const [apiKeyOverview, setApiKeyOverview] = useState<Record<string, { configured?: boolean; lastFour?: string | null }>>(
    {}
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminSystemConfiguration();
      const g = data?.globalSettings || {};
      setSiteDisplayName(String(g.siteDisplayName ?? ''));
      setSupportEmail(String(g.supportEmail ?? ''));
      setMaintenanceMode(Boolean(g.maintenanceMode));
      setMaintenanceMessage(String(g.maintenanceMessage ?? ''));

      const integ = data?.integrations || {};
      setOutboundWebhookUrl(String(integ.outboundWebhookUrl ?? ''));
      setSlackIncomingWebhook(String(integ.slackIncomingWebhook ?? ''));
      setCustomIntegrationNotes(String(integ.customIntegrationNotes ?? ''));

      const ft = data?.featureToggles || {};
      setAiAgent(ft.aiAgent !== false);
      setPayments(ft.payments !== false);
      setHelpChat(ft.helpChat !== false);
      setSwaggerUi(ft.swaggerUi !== false);

      setApiKeyOverview(data?.apiKeysOverview || {});
      setApiKeyInputs({ openaiApiKey: '', stripeSecretKey: '', outboundApiToken: '' });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const apiKeys: Record<string, string> = {};
      API_KEY_FIELDS.forEach(({ key }) => {
        const v = (apiKeyInputs[key] || '').trim();
        if (v) apiKeys[key] = v;
      });

      await apiService.updateAdminSystemConfiguration({
        globalSettings: {
          siteDisplayName: siteDisplayName.trim(),
          supportEmail: supportEmail.trim(),
          maintenanceMode,
          maintenanceMessage: maintenanceMessage.trim(),
        },
        integrations: {
          outboundWebhookUrl: outboundWebhookUrl.trim(),
          slackIncomingWebhook: slackIncomingWebhook.trim(),
          customIntegrationNotes: customIntegrationNotes.trim(),
        },
        featureToggles: {
          aiAgent,
          payments,
          helpChat,
          swaggerUi,
        },
        apiKeys: Object.keys(apiKeys).length ? apiKeys : undefined,
      });
      setSuccess('Saved');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SettingsIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          System Configuration
        </Typography>
      </Box>

      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Global settings
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
          <TextField
            size="small"
            label="Site display name"
            value={siteDisplayName}
            onChange={(e) => setSiteDisplayName(e.target.value)}
          />
          <TextField
            size="small"
            label="Support email"
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={maintenanceMode} onChange={(e) => setMaintenanceMode(e.target.checked)} />}
            label="Maintenance mode"
          />
          <TextField
            size="small"
            label="Maintenance message"
            multiline
            minRows={2}
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          API key management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Values are stored server-side; only the last four characters are shown after save. Leave blank to keep the current key.
        </Typography>
        {API_KEY_FIELDS.map(({ key, label }) => {
          const ov = apiKeyOverview[key];
          const hint = ov?.configured ? `Current key ends with …${ov.lastFour || ''}` : 'Not set';
          return (
            <Box key={key} sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">{hint}</Typography>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={label}
                placeholder="Enter new key to replace"
                value={apiKeyInputs[key] || ''}
                onChange={(e) => setApiKeyInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                sx={{ mt: 0.5 }}
              />
            </Box>
          );
        })}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Integration configs
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
          <TextField
            size="small"
            label="Outbound webhook URL"
            value={outboundWebhookUrl}
            onChange={(e) => setOutboundWebhookUrl(e.target.value)}
          />
          <TextField
            size="small"
            label="Slack incoming webhook URL"
            value={slackIncomingWebhook}
            onChange={(e) => setSlackIncomingWebhook(e.target.value)}
          />
          <TextField
            size="small"
            label="Notes (internal)"
            multiline
            minRows={2}
            value={customIntegrationNotes}
            onChange={(e) => setCustomIntegrationNotes(e.target.value)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Feature toggles
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <FormControlLabel control={<Switch checked={aiAgent} onChange={(e) => setAiAgent(e.target.checked)} />} label="AI Agent" />
          <FormControlLabel control={<Switch checked={payments} onChange={(e) => setPayments(e.target.checked)} />} label="Payments" />
          <FormControlLabel control={<Switch checked={helpChat} onChange={(e) => setHelpChat(e.target.checked)} />} label="Help chat" />
          <FormControlLabel control={<Switch checked={swaggerUi} onChange={(e) => setSwaggerUi(e.target.checked)} />} label="Swagger UI (public docs)" />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Toggle state is persisted for future use across services. Individual features can read these flags in follow-up work.
        </Typography>
      </Paper>

      <Button variant="contained" disabled={saving || loading} onClick={save}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
      <Button sx={{ ml: 1 }} variant="outlined" disabled={saving || loading} onClick={load}>
        Reload
      </Button>
    </Box>
  );
};

export default SuperAdminSystemConfigurationPage;
